<template>
    <transition name="menu-drop">
        <!-- touchmove.self.prevent: a pan starting on the scrim (the wrap itself)
             must not scroll the menu behind — the document stays scrollable while
             overlays are open (see navLock.js). Pans inside the panel still work. -->
        <div class="menu-category-all-wrap" @click="close" @touchmove.self.prevent>
            <div class="menu-category-all" @click.stop>
                <div class="menu-category-all-header">
                    <div class="menu-category-all-title">
                        {{ $t("pages.menu.blocks.categories.title") }}
                    </div>
                    <div class="menu-category-all-close" @click="close">
                        <IconBase width="26" height="26" view-box="0 0 22 22">
                            <IconPlus />
                        </IconBase>
                    </div>
                </div>
                <ul class="menu-category-all-ul">
                    <li class="menu-category-all-button" :class="{ 'selected': category.id == selectedCategory }"
                        v-for="category in categories" :key="category.id" @click="scrollMenu(category.id)">
                        {{ category.name }}
                        <IconBase v-show="category.id == selectedCategory" width="20" height="20" view-box="0 0 22 22">
                            <IconCheckmark />
                        </IconBase>
                    </li>
                </ul>
            </div>
        </div>
    </transition>
</template>

<script>
import IconBase from '@/components/IconBase.vue';
import IconCheckmark from '@/components/icons/IconCheckmark.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import { lockNav, unlockNav } from "@/utils/navLock";

export default {
    name: "MenuInfScroll",
    components: {
        IconBase,
        IconCheckmark,
        IconPlus,
    },
    props: {
        isShown: { type: Boolean, required: true },
        categories: { type: Array, required: true },
        selectedCategory: { type: [String, Number], required: true },
    },
    mounted() {
        // Teleport to <body>: this overlay lives inside `.menu-header`, which has
        // its own `backdrop-filter`. That makes the header a "backdrop root", so a
        // nested backdrop-filter can only sample content *within* the header — the
        // page/menu behind it never gets blurred (only the flat scrim showed).
        // Moving it to <body> escapes that boundary so the blur works (same trick
        // the dish-details sheet uses). Paired with `position: fixed` in CSS.
        document.body.appendChild(this.$el);
    },
    beforeDestroy() {
        // Remove the teleported root from <body> (Vue removes from the original
        // parent, so do it explicitly to avoid an orphan node).
        if (this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
    },
    watch: {
        isShown(newVal) {
            // lockNav also freezes document scroll while the overlay is open.
            if (newVal) {
                lockNav();
            } else {
                unlockNav();
            }
        }
    },
    methods: {
        scrollMenu(id) {
            this.$emit("scrollMenu", id);
            this.close();
        },
        close() {
            this.$emit("close");
        }
    }
}
</script>

<style lang="scss" scoped>
ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
}

/* Scrim fades in while the panel slides down from above the top edge ("висовується
   зверху"). Both run at --dur-base; Vue times the transition off the scrim root, so
   the panel's transform must share that duration to finish together. */
.menu-drop-enter-active,
.menu-drop-leave-active {
    transition: opacity var(--dur-base) var(--ease-standard);

    .menu-category-all {
        transition: transform var(--dur-base) var(--ease-out);
    }
}

.menu-drop-enter,
.menu-drop-leave-to {
    opacity: 0;

    .menu-category-all {
        transform: translateY(-100%);
    }
}

.menu-category-all-wrap {
    /* Fixed (not absolute): the overlay is teleported to <body> in mounted() to
       escape `.menu-header`'s backdrop-filter, so it pins to the viewport itself.
       z-index must clear the sticky header (1100), now that we're a body sibling. */
    position: fixed;
    top: 0;
    left: 0;

    width: 100%;
    height: 100vh;

    z-index: 2000;
    user-select: none;

    /* Same backdrop recipe as the dish-details sheet: a theme-aware dark scrim
       (0.45 light / 0.62 dark) over a 6px blur, so the page reads as pushed back
       behind the panel. The scrim is semi-transparent so the blur shows through. */
    background: var(--overlay-scrim);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
}

.menu-category-all {
    overflow: auto;
    overscroll-behavior: contain;   /* don't chain panel scroll into the page */
    max-height: calc(100vh - 100px);

    /* extra top pad keeps the header clear of the status bar / Dynamic Island —
       the panel slides down from the very top edge (viewport-fit=cover) */
    padding: calc(12px + var(--safe-top, 0px)) 12px 12px;

    border-bottom: 1px solid var(--border-subtle);
    border-radius: 0 0 var(--radius-2xl) var(--radius-2xl);
    box-shadow: var(--shadow-md);

    background: var(--surface-card);
}

.menu-category-all-header {
    position: relative;

    display: flex;
    justify-content: center;
    padding: 10px;

    .menu-category-all-title {
        font-weight: var(--weight-semibold);
        font-size: var(--text-lg);
        line-height: 1;

        color: var(--text-primary);
    }

    .menu-category-all-close {
        position: absolute;
        right: 0;
        top: 50%;

        transform: translate(0, -50%) rotate(45deg);
    }
}


.menu-category-all-ul {
    display: grid;
    gap: 8px;

    width: 100%;
}

.menu-category-all-button {
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 11px 16px;

    font-weight: var(--weight-bold);
    font-size: 14px;
    text-transform: capitalize;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);

    color: var(--text-secondary);
    background: var(--surface-card);
    cursor: pointer;
}

.selected {
    color: #fff;
    background: var(--grad-brand);
    border-color: transparent;
    box-shadow: 0 6px 14px var(--sm-glow);
}
</style>